class ImageSlider extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.currentIndex = 0;
    this.images = [];
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchMoveX = 0;
    this.touchMoveY = 0;
    this.isScrolling = false;

    // Create a MutationObserver to watch for changes in the light DOM
    this.observer = new MutationObserver(() => this.updateImages());
  }

  connectedCallback() {
    this.updateImages();
    this.render();
    this.setupEventListeners();
    
    // Observe changes in the light DOM
    this.observer.observe(this, { childList: true, subtree: true });

    // Use ResizeObserver to detect size changes
    this.resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        if (entry.contentBoxSize) {
          this.updateSize();
        }
      }
    });
    this.resizeObserver.observe(this);

    // Force initial layout after a short delay
    setTimeout(() => this.updateSize(), 0);

    console.log('ImageSlider connected. Dimensions:', this.offsetWidth, this.offsetHeight);
  }

  disconnectedCallback() {
    this.resizeObserver.disconnect();
    this.observer.disconnect();
  }

  updateImages() {
    this.images = Array.from(this.querySelectorAll('img'));
    console.log('Updated image sources:', this.images.map(img => img.src));
    this.render();
    this.updateSize();
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          overflow: hidden;
          position: relative;
        }
        .slider-container {
          display: flex;
          transition: transform 0.3s ease-out;
        }
        img {
          width: 100%;
          height: 100%;
          flex-shrink: 0;
          object-fit: cover;
        }
        .dots {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          z-index: 1;
        }
        .dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.5);
          margin: 0 5px;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }
        .dot.active {
          background-color: white;
        }
        .nav-button {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background-color: rgba(255, 255, 255, 0.5);
          border: none;
          font-size: 24px;
          padding: 10px;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }
        .nav-button:hover {
          background-color: rgba(255, 255, 255, 0.8);
        }
        .prev-button {
          left: 10px;
        }
        .next-button {
          right: 10px;
        }
      </style>
      <div class="slider-container">
        ${this.images.map(img => `<img src="${img.src}" alt="${img.alt}">`).join('')}
      </div>
      <div class="dots">
        ${this.images.map((_, i) => `<div class="dot${i === 0 ? ' active' : ''}"></div>`).join('')}
      </div>
      <button class="nav-button prev-button">&lt;</button>
      <button class="nav-button next-button">&gt;</button>
    `;
  
    // Set up event listeners for navigation buttons
    this.shadowRoot.querySelector('.prev-button').addEventListener('click', () => this.goToSlide(this.currentIndex - 1));
    this.shadowRoot.querySelector('.next-button').addEventListener('click', () => this.goToSlide(this.currentIndex + 1));
  }

  updateSize() {
    const containerWidth = this.offsetWidth;
    const containerHeight = this.offsetHeight;
    const sliderContainer = this.shadowRoot.querySelector('.slider-container');
    const imgs = sliderContainer.querySelectorAll('img');
    
    console.log('Container dimensions:', containerWidth, containerHeight);
    console.log('Number of images:', imgs.length);
  
    if (containerWidth === 0 || containerHeight === 0) {
      console.warn('ImageSlider: Container has zero width or height');
      return;
    }
  
    sliderContainer.style.width = `${containerWidth * this.images.length}px`;
    sliderContainer.style.height = `${containerHeight}px`;
  
    imgs.forEach((img, index) => {
      img.style.width = `${containerWidth}px`;
      img.style.height = `${containerHeight}px`;
      console.log(`Image ${index + 1} dimensions:`, img.offsetWidth, img.offsetHeight);
    });
    
    this.goToSlide(this.currentIndex, false);
  }

setupEventListeners() {
  const sliderContainer = this.shadowRoot.querySelector('.slider-container');
  const dots = this.shadowRoot.querySelectorAll('.dot');

  sliderContainer.addEventListener('mousedown', this.startDragging.bind(this));
  sliderContainer.addEventListener('touchstart', this.startDragging.bind(this), { passive: true });
  window.addEventListener('mousemove', this.drag.bind(this));
  window.addEventListener('touchmove', this.drag.bind(this), { passive: false });
  window.addEventListener('mouseup', this.stopDragging.bind(this));
  window.addEventListener('touchend', this.stopDragging.bind(this));
  window.addEventListener('mouseleave', this.stopDragging.bind(this));

  dots.forEach((dot, index) => {
    dot.addEventListener('click', () => this.goToSlide(index));
  });
}

startDragging(e) {
  if (e.type.includes('mouse')) {
    e.preventDefault();
    this.isDragging = true;
    this.startPos = this.getPositionX(e);
  } else {
    // Touch event
    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].clientY;
  }
  this.shadowRoot.querySelector('.slider-container').style.transition = 'none';
}

drag(e) {
  if (e.type.includes('mouse')) {
    if (!this.isDragging) return;
    e.preventDefault();
    const currentPosition = this.getPositionX(e);
    const diff = currentPosition - this.startPos;
    this.currentTranslate = this.prevTranslate + diff;
    this.setSliderPosition();
  } else {
    // Touch event
    this.touchMoveX = e.touches[0].clientX;
    this.touchMoveY = e.touches[0].clientY;
    const diffX = this.touchMoveX - this.touchStartX;
    const diffY = this.touchMoveY - this.touchStartY;

    if (!this.isScrolling) {
      if (Math.abs(diffX) > Math.abs(diffY)) {
        // Horizontal drag
        e.preventDefault();
        this.isDragging = true;
        this.currentTranslate = this.prevTranslate + diffX;
        this.setSliderPosition();
      } else {
        // Vertical scroll
        this.isScrolling = true;
      }
    }
  }
}

stopDragging() {
  if (!this.isDragging) return;
  this.isDragging = false;
  this.isScrolling = false;
  const movedBy = this.currentTranslate - this.prevTranslate;

  if (Math.abs(movedBy) > this.offsetWidth / 4) {
    if (movedBy < 0 && this.currentIndex < this.images.length - 1) {
      this.currentIndex += 1;
    } else if (movedBy > 0 && this.currentIndex > 0) {
      this.currentIndex -= 1;
    }
  }

  this.goToSlide(this.currentIndex);
}
  
  getPositionX(e) {
    return e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
  }
  
  setSliderPosition() {
    this.shadowRoot.querySelector('.slider-container').style.transform = `translateX(${this.currentTranslate}px)`;
  }

  goToSlide(index, animate = true) {
    this.currentIndex = Math.max(0, Math.min(index, this.images.length - 1));
    const containerWidth = this.offsetWidth;
    this.prevTranslate = this.currentTranslate = -this.currentIndex * containerWidth;
    const sliderContainer = this.shadowRoot.querySelector('.slider-container');
    sliderContainer.style.transition = animate ? 'transform 0.3s ease-out' : 'none';
    this.setSliderPosition();
    this.updateDots();
  }

  updateDots() {
    const dots = this.shadowRoot.querySelectorAll('.dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentIndex);
    });
  }
}

customElements.define('image-slider', ImageSlider);